import { Component, inject, OnInit, signal, effect, computed, input, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DashboardLayoutEditorComponent } from '../../../../shared/components/dashboard/dashboard-layout-editor.component';
import { ApiService } from '../../../../core/services/api.service';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import {
  DashboardPageKey,
  LendeeDashboardSectionId,
  LendeeDashboardSectionDef,
} from '../../../../core/enums/dashboard-cards.enum';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilterByWidgetKeyMap, findCatalogPageId, resolveActiveCatalogProductId } from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  getLiveSectionColClass,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { TooltipComponent } from '../../../../shared/components/ui/tooltip/tooltip.component';
import { DatePickerComponent, type DateRangePickerOutput } from '../../../../shared/components/date-picker/date-picker.component';
import { IndividualToasterComponent } from "../../../../shared/components/loader/loader.component";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

dayjs.extend(utc);
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';

interface RequestStats {
  total_requests: number;
  public_requests: number;
  private_requests: number;
  cancelled_requests: number;
  expired_requests: number;
  cancelled_requests_after_offer: number;
}

interface OfferStats {
  total_offers: number;
  offers_accepted: number;
  offers_pending: number;
  cancelled_offers: number;
  lacks_funds: number;
  cancelled_by_supporter: number;
}

interface PaymentPlanStats {
  plans_created: number;
  plans_completed_today: number;
  plans_overdue_today: number;
  plans_overdue_total: number;
}

interface OverdueStats {
  overdue_less_than_30: number;
  overdue_30_to_60: number;
  overdue_60_to_90: number;
  overdue_90_plus: number;
}

interface DashboardStats {
  requests: RequestStats;
  offers: OfferStats;
  paymentPlans: PaymentPlanStats;
  overdue: OverdueStats;
}

interface SectionLayoutItem {
  id: LendeeDashboardSectionId;
  visible: boolean;
  colSpan: number;
  order: number;
}

interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

@Component({
  selector: 'app-lendee-dashboard',
  standalone: true,
  imports: [CommonModule, DashboardLayoutEditorComponent, IconComponent, TooltipComponent, DatePickerComponent, IndividualToasterComponent],
  templateUrl: './lendee-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class LendeeDashboardComponent implements OnInit {
  private apiService = inject(ApiService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);
  readonly PAGE_KEY = DashboardPageKey.LENDEE_DASHBOARD;
  readonly SectionId = LendeeDashboardSectionId;
  readonly SECTIONS = LendeeDashboardSectionDef;

  isLoading = signal(false);
  // Date range state - Initialize with last 7 days
  public dateRange = signal((() => {
    const nowInLA = dayjs().tz(LA_TIMEZONE);
    const sevenDaysAgo = nowInLA.subtract(6, 'day');
    
    return {
      startDate: sevenDaysAgo.format('MM/DD/YYYY'),
      endDate: nowInLA.format('MM/DD/YYYY')
    };
  })());

  public stats = signal<DashboardStats>({
    requests: {
      total_requests: 0,
      public_requests: 0,
      private_requests: 0,
      cancelled_requests: 0,
      expired_requests: 0,
      cancelled_requests_after_offer: 0
    },
    offers: {
      total_offers: 0,
      offers_accepted: 0,
      offers_pending: 0,
      cancelled_offers: 0,
      lacks_funds: 0,
      cancelled_by_supporter: 0
    },
    paymentPlans: {
      plans_created: 0,
      plans_completed_today: 0,
      plans_overdue_today: 0,
      plans_overdue_total: 0
    },
    overdue: {
      overdue_less_than_30: 0,
      overdue_30_to_60: 0,
      overdue_60_to_90: 0,
      overdue_90_plus: 0
    }
  });

  constructor() {
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      const productPid = this.apiService.selectedProductPid();
      if (!productId && productPid == null) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<LendeeDashboardSectionId>();
          keys.forEach((k) => {
            const mapped = this.WIDGET_KEY_TO_SECTION.get(k);
            if (mapped != null) {
              pinned.add(mapped);
              return;
            }
            if (Object.values(LendeeDashboardSectionId).includes(k as LendeeDashboardSectionId)) {
              pinned.add(k as LendeeDashboardSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<LendeeDashboardSectionId, number>();
            for (const sec of Object.values(LendeeDashboardSectionId)) {
              const mappedKey = this.SECTION_WIDGET_KEYS[sec];
              const id =
                (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  ngOnInit(): void {
    this._initLayout();
    if (this.mixedMode()) {
      this.fetchStats();
    }
  }

  readonly layoutMap = signal<Record<LendeeDashboardSectionId, SectionLayoutItem>>(
    {} as Record<LendeeDashboardSectionId, SectionLayoutItem>,
  );

  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  /**
   * Frontend section id (`lend_*`) → backend catalog `widget_key`
   * (`le_ln_*`) mapping. Backend catalog under product Lendee
   * (`pages.id = 37`, slug `dashboard`) registers widgets with
   * `le_ln_` prefixes that differ from the frontend enum values.
   */
  private readonly SECTION_WIDGET_KEYS: Partial<Record<LendeeDashboardSectionId, string>> = {
    [LendeeDashboardSectionId.REQUESTS_CARD]:           'le_ln_applications_pipeline',
    [LendeeDashboardSectionId.OFFERS_CARD]:             'le_ln_offers_responses',
    [LendeeDashboardSectionId.PAYMENT_PLANS_CARD]:      'le_ln_payment_plans',
    [LendeeDashboardSectionId.RISK_ASSESSMENT]:         'le_ln_risk_matrix',
    [LendeeDashboardSectionId.OPERATIONAL_EFFICIENCY]:  'le_ln_operational_efficiency',
    [LendeeDashboardSectionId.KEY_PERFORMANCE_METRICS]: 'le_ln_kpi_metrics',
  };

  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<string, LendeeDashboardSectionId> =
    new Map(
      (Object.entries(this.SECTION_WIDGET_KEYS) as [
        LendeeDashboardSectionId,
        string,
      ][]).map(([sec, key]) => [key, sec]),
    );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  showSection(sectionId: LendeeDashboardSectionId): boolean {
    return this.embeddedVisibleSections().some((s) => s.id === sectionId);
  }

  sectionOrder(sectionId: LendeeDashboardSectionId): number {
    return this.layoutMap()[sectionId]?.order ?? 0;
  }

  getSectionColClass(sectionId: LendeeDashboardSectionId): string {
    if (this.mixedMode()) return 'col-span-12';
    const span =
      this.layoutMap()[sectionId]?.colSpan ??
      this.DEFAULT_COL_SPAN[sectionId] ??
      12;
    return getLiveSectionColClass(span);
  }

  /** Card shell — drops duplicate chrome when embedded in My Dashboard. */
  sectionCardShellClass(withHover = true): string {
    if (this.mixedMode()) {
      return 'h-full flex flex-col min-h-0 relative group';
    }
    const base =
      'card-bg rounded-2xl p-6 relative group flex flex-col h-full';
    return withHover
      ? `${base} hover:shadow-2xl hover:-translate-y-1 duration-300`
      : base;
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  /** Full-width sections render taller in the Customize Layout mini-grid preview. */
  isLayoutEditorTallTile = (item: { id: string }): boolean =>
    item.id === LendeeDashboardSectionId.RISK_ASSESSMENT;

  rowDisplayOrder(sectionIds: LendeeDashboardSectionId[]): number {
    const orders = sectionIds
      .filter((id) => this.showSection(id))
      .map((id) => this.sectionOrder(id));
    return orders.length ? Math.min(...orders) : 9999;
  }

  /** Preserves original xl:grid-cols-3 row for Requests / Offers / Payment Plans. */
  metricsRowGridClass(sectionIds: LendeeDashboardSectionId[]): string {
    const visibleCount = sectionIds.filter((id) => this.showSection(id)).length;
    if (visibleCount <= 1) return 'grid grid-cols-1 gap-6';
    if (visibleCount === 2) return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6';
    return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6';
  }

  summaryPairGridClass(sectionIds: LendeeDashboardSectionId[]): string {
    const visibleCount = sectionIds.filter((id) => this.showSection(id)).length;
    return visibleCount <= 1
      ? 'grid grid-cols-1 gap-6'
      : 'grid grid-cols-1 lg:grid-cols-2 gap-6';
  }

  readonly pinnedSections = signal<Set<LendeeDashboardSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<LendeeDashboardSectionId>>(new Set());

  readonly pinnableWidgetIds = signal<Map<LendeeDashboardSectionId, number>>(
    new Map(),
  );

  isAddedToCustomDashboard(sectionId: LendeeDashboardSectionId): boolean {
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(sectionId: LendeeDashboardSectionId): boolean {
    return this.pendingPinSections().has(sectionId);
  }

  addToCustomDashboard(
    sec: { id: LendeeDashboardSectionId } | LendeeDashboardSectionId,
  ): void {
    const sectionId =
      typeof sec === 'object' && sec !== null ? sec.id : sec;
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const colSpan = this._normalizeColSpan(
      this.layoutMap()[sectionId]?.colSpan ??
        this.DEFAULT_COL_SPAN[sectionId] ??
        12,
    );
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);
    const productId = resolveActiveCatalogProductId(this.apiService);
    if (productId == null) return;

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const fire = (resolvedPageId: number) => {
      const widgetId = this.pinnableWidgetIds().get(sectionId);
      if (widgetId != null) {
        this.customDashboardPin.toggleLayoutItem({
          pageId: resolvedPageId,
          widgetId,
          sourcePageId: resolvedPageId,
          colSpan,
          rowSpan: 1,
          customConfig: {},
          productId,
          isCurrentlyPinned,
          onPinnedChange,
          onPendingChange,
        });
        return;
      }

      this.customDashboardPin.toggleSection({
        pageId: resolvedPageId,
        widgetKey: this.SECTION_WIDGET_KEYS[sectionId] ?? sectionId,
        colSpan,
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
    };

    const cachedPageId = this.effectiveCatalogPageId();
    if (cachedPageId != null) {
      fire(cachedPageId);
      return;
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId = findCatalogPageId(
          catalog as any[],
          this.PAGE_KEY,
          productId,
        );

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        this.effectiveCatalogPageId.set(resolvedPageId);

        if (this.pinnableWidgetIds().get(sectionId) == null) {
          this.customDashboardPin
            .resolveCatalogWidgetIdsForPage(resolvedPageId, productId)
            .subscribe((widgetIdMap) => {
              const next = new Map(this.pinnableWidgetIds());
              for (const id of Object.values(LendeeDashboardSectionId)) {
                const mappedKey = this.SECTION_WIDGET_KEYS[id];
                const wid =
                  (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                  widgetIdMap.get(id);
                if (wid != null) next.set(id, wid);
              }
              this.pinnableWidgetIds.set(next);
              fire(resolvedPageId);
            });
          return;
        }

        fire(resolvedPageId);
      });
  }

  private _warnCatalogMiss(
    catalog: any[],
    productId: string | number | null,
  ): void {
    const scopedProducts =
      productId != null && String(productId).trim() !== ''
        ? (catalog ?? []).filter(
            (p: any) => String(p?.id) === String(productId),
          )
        : (catalog ?? []);

    const productsForLog =
      scopedProducts.length > 0 ? scopedProducts : (catalog ?? []);

    const pageNames: string[] = [];
    for (const product of productsForLog) {
      for (const page of product?.pages ?? []) {
        const name = String(page?.name ?? '').trim();
        if (name) pageNames.push(name);
      }
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[LendeeDashboard] No catalog page found for ` +
        `"${this.PAGE_KEY}" (active product=${productId}). ` +
        `Catalog advertised pages for this product: ` +
        `${pageNames.length ? pageNames.join(', ') : '(none)'}. ` +
        `If the correct page is in the list above, add a matching ` +
        `substring to CATALOG_PAGE_HINTS[LENDEE_DASHBOARD] ` +
        `in dashboard-embed.helpers.ts.`,
    );
  }

  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );
  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  private _layoutSnapshot: Record<LendeeDashboardSectionId, SectionLayoutItem> | null =
    null;

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];
  private readonly LAYOUT_LS_KEY = 'prism_lendee_dashboard_layout_v1';

  private readonly DEFAULT_COL_SPAN: Record<LendeeDashboardSectionId, number> = {
    [LendeeDashboardSectionId.REQUESTS_CARD]: 4,
    [LendeeDashboardSectionId.OFFERS_CARD]: 4,
    [LendeeDashboardSectionId.PAYMENT_PLANS_CARD]: 4,
    [LendeeDashboardSectionId.RISK_ASSESSMENT]: 12,
    [LendeeDashboardSectionId.OPERATIONAL_EFFICIENCY]: 6,
    [LendeeDashboardSectionId.KEY_PERFORMANCE_METRICS]: 6,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    this.editorItems.set(
      this.orderedSections().map((s) => ({
        id: s.id,
        title: titleById.get(s.id) ?? s.id,
        visible: s.visible,
        colSpan: this._normalizeColSpan(s.colSpan),
        order: s.order,
        defaultColSpan: this._normalizeColSpan(
          this.DEFAULT_COL_SPAN[s.id as LendeeDashboardSectionId] ?? 12,
        ),
      })),
    );
    this._layoutSnapshot = JSON.parse(JSON.stringify(this.layoutMap()));
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.layoutMap.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: LendeeDashboardSectionId): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    this.editorItems.set(
      this.SECTIONS.map((s, idx) => {
        const defaultSpan = this._normalizeColSpan(
          this.DEFAULT_COL_SPAN[s.id as LendeeDashboardSectionId] ?? 12,
        );
        return {
          id: s.id as LendeeDashboardSectionId,
          title: s.title,
          visible: true,
          colSpan: defaultSpan,
          order: idx,
          defaultColSpan: defaultSpan,
        };
      }),
    );
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout = this._buildLayoutFromEditor();
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);
    this._layoutSnapshot = null;

    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    const toBackendKeysDraft = (resolvedPageId: number) =>
      draft.map((d, idx) => ({
        key: this.SECTION_WIDGET_KEYS[d.id] ?? String(d.id),
        sourcePageId: resolvedPageId,
        colSpan: this._normalizeColSpan(d.colSpan),
        visible: d.visible,
        customConfig: null,
        order: idx,
      }));

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d, idx) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId =
          cachedPageId ??
          findCatalogPageId(catalog as any[], this.PAGE_KEY, productId);

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        if (cachedPageId == null) {
          this.effectiveCatalogPageId.set(resolvedPageId);
        }

        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: resolvedPageId,
          productId,
          fallbackSourcePageId: resolvedPageId,
          draft: toBackendKeysDraft(resolvedPageId),
        });
      });
  }

  decreaseEditorCardColSpan(id: LendeeDashboardSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[i.id] ?? 12);
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: LendeeDashboardSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(
      this.editorItems(),
      (i) => this.isLayoutEditorTallTile(i as LayoutEditorItem),
    );
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[item.id] ?? 12);
    return this.COL_SPAN_STEPS[idx - 1] >= minSpan;
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }
   /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  } 

  private _buildLayoutFromEditor(): Record<LendeeDashboardSectionId, SectionLayoutItem> {
    return buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this.SECTIONS as readonly { id: LendeeDashboardSectionId }[],
      this.DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
  }

  private _syncLayoutFromEditor(): void {
    if (!this.isLayoutEditorOpen()) return;
    this.layoutMap.set(this._buildLayoutFromEditor());
  }

  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<LendeeDashboardSectionId, SectionLayoutItem> =
      {} as Record<LendeeDashboardSectionId, SectionLayoutItem>;
    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as LendeeDashboardSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12),
        order: storedItem?.order ?? idx,
      };
    });
    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(layout: Record<LendeeDashboardSectionId, SectionLayoutItem>): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // ignore
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  private fetchStats() {
    this.isLoading.set(true);
    const payload = {
      from: this.dateRange().startDate,
      to: this.dateRange().endDate,
      is_dummy: 0,
      filter_type: 1,
    };

    this.apiService
      .post('/admin/product_metrics_stats', payload, {
        addAccessToken: false,
        productServerName: 'lendee',
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
        headers: {
          'timezone': '-700'
        }
      })
      .subscribe({
        next: (response: any) => {
          this.stats.set({
            requests: {
              total_requests: response.request?.total_requests || 0,
              public_requests: response.request?.public_requests || 0,
              private_requests: response.request?.private_requests || 0,
              cancelled_requests: response.request?.cancelled_requests || 0,
              expired_requests: response.request?.expired_requests || 0,
              cancelled_requests_after_offer: response.request?.cancelled_requests_after_offer || 0,
            },
            offers: {
              total_offers: response.offers?.total_offers || 0,
              offers_accepted: response.offers?.offers_accepted || 0,
              offers_pending: response.offers?.offers_pending || 0,
              cancelled_offers: response.offers?.cancelled_offers || 0,
              lacks_funds: response.offers?.lacks_funds || 0,
              cancelled_by_supporter: response.offers?.cancelled_by_supporter || 0,
            },
            paymentPlans: {
              plans_created: response.paymentPlans?.plans_created || 0,
              plans_completed_today: response.paymentPlans?.plans_completed_today || 0,
              plans_overdue_today: response.paymentPlans?.plans_overdue_today || 0,
              plans_overdue_total: response.paymentPlans?.plans_overdue_total || 0,
            },
            overdue: {
              overdue_less_than_30: response.overdue?.overdue_less_than_30 || 0,
              overdue_30_to_60: response.overdue?.overdue_30_to_60 || 0,
              overdue_60_to_90: response.overdue?.overdue_60_to_90 || 0,
              overdue_90_plus: response.overdue?.overdue_90_plus || 0,
            }
          });
          this.isLoading.set(false)
        },
        error: (error: any) => {
          console.error('API Error:', error);
          this.isLoading.set(false)
        },
      });
  }

  onDateRangeChange(output: DateRangePickerOutput): void {
    const startDate = dayjs.utc(output.start_date).tz(LA_TIMEZONE);
    const endDate = dayjs.utc(output.end_date).tz(LA_TIMEZONE);
    
    this.dateRange.set({
      startDate: startDate.format('MM/DD/YYYY'),
      endDate: endDate.format('MM/DD/YYYY')
    });

    // Fetch new data with updated date range
    this.fetchStats();
  }
}
